/**
 * Ask AI — the batch tutor on a session page.
 *
 * One conversation per student per module, kept by the tutor on the mentor's
 * laptop (ai-tutor-v2 docs/integration/BATCH_VIDEO_TUTOR.md). Each question is
 * sent with where the student is in the recording, so "what is he drawing
 * here?" means this moment of this class. Answers cite their sources — a class
 * and time (click to jump there), a reading section or a document.
 *
 * The chat itself (polling, images, history, clearing) is ../Tutor/TutorChat,
 * shared with the reading page's book tutor.
 */

import React, { useMemo } from "react";

import TutorTabs from "../Tutor/TutorTabs";
import {
  askTutor, askTutorThread, clearTutor, createTutorThread, getTutorHistory, getTutorJob, getTutorStatus, getTutorThreadHistory,
  getTutorThreads, updateTutorThread,
} from "./sessionsApi";

export { MAX_IMAGES, Message, distinctSources, shrinkImage, sourceLabel, splitAttached } from "../Tutor/TutorChat";

export default function SessionTutor({ sessionId, moduleId, playerRef, onSource, kind = "template" }) {
  const api = useMemo(() => ({
    ask: (body) => askTutor(sessionId, body),
    job: (token) => getTutorJob(sessionId, token),
    history: (before) => getTutorHistory(sessionId, before),
    clear: () => clearTutor(sessionId),
    status: () => getTutorStatus(sessionId),
    // Side conversations: tabs branched from a passage of an answer.
    threads: () => getTutorThreads(sessionId),
    createThread: (body) => createTutorThread(sessionId, body),
    updateThread: (id, patch) => updateTutorThread(sessionId, id, patch),
    askThread: (id, body) => askTutorThread(sessionId, id, body),
    threadHistory: (id, before) => getTutorThreadHistory(sessionId, id, before),
  }), [sessionId]);

  const context = () => {
    const control = playerRef?.current;
    if (!control) return {};
    const player = { recording_id: control.recordingId, t: Math.round(control.time() * 10) / 10 };
    return { payload: { player }, bubble: { player } };
  };

  const hasPlayer = !!playerRef?.current;
  // A 1:1 class: the tutor can't see the recording (it isn't transcribed); it
  // answers from its knowledge and remembers this card's conversation.
  const labels = kind === "topic"
    ? {
        online: "Ask about anything in this class",
        empty: "Ask anything about what your mentor taught in this class. The AI can't see the recording, so tell it what was said or shown; it remembers this conversation.",
        footer: "Your mentor can see this conversation.",
      }
    : {
        online: "Ask about this module's classes and reading",
        empty: "Ask anything about this module — what the mentor just explained, a diagram on the board, or a chapter of the reading.",
        footer: `${hasPlayer ? "Your question is asked about the moment you're watching. " : ""}Your mentor can see this conversation.`,
      };
  return <TutorTabs api={api} pendingId={moduleId} context={context} onSource={onSource} labels={labels} />;
}
